import React from "react";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {Tooltip} from "antd";
import {
    AutoSizer, List, ListRowProps, Size,
} from "react-virtualized";
import "./TaskList.scss";
import Nullable from "@data-force/app/interfaces/nullable";
import {EditorType} from "@data-force/app";
import {ElementsValuesMap} from "@data-force/app/interfaces/store/reducers/schema";
import StoreState from "../../../interfaces/store/storeState";
import {
    StatusIconData, StatusIconInfo, Task, TaskEntity,
} from "../../../interfaces/store/actions/payload/task";
import {getHistoryParams, getStatusIconData, isTaskMetadataValid} from "../../../utils/common";
import HistoryParams from "../../../interfaces/historyParams";
import {SelectTaskInWizardAction} from "../../../interfaces/store/actions/task";
import {selectTaskInWizard} from "../../../store/actions/task";
import {GetFormErrorsPayload} from "../../../interfaces/store/actions/payload/job";
import {ENTER_KEY, SPACE_KEY} from "../../../utils/constants";

interface StateProps {
    translate: TranslateFunction
    tasks: TaskEntity[]
    activeTask: Nullable<Task>
    editor: Nullable<EditorType>
    elementsValuesMap: ElementsValuesMap
    validTaskMetadata: boolean
}

interface DispatchProps {
    selectTaskInWizard(payload: string): SelectTaskInWizardAction
}

type Props = StateProps & DispatchProps;

class TaskList extends React.Component<Props> {
    private static readonly translationPath: string = "components.TaskList.TaskList";

    private static readonly rowHeight: number = 40;

    constructor(props: Props) {
        super(props);

        this.tasksRowRenderer = this.tasksRowRenderer.bind(this);
        this.createStatusIcon = this.createStatusIcon.bind(this);
    }

    /**
     * Select active task
     *
     * @param {React.KeyboardEvent} event
     * @param {string} payload
     * @return {void}
     */
    onSelectTask(payload: string, event: React.KeyboardEvent): void {
        if ([ENTER_KEY, SPACE_KEY].includes(event.key)) {
            this.props.selectTaskInWizard(payload);
        }
    }

    /**
     * Selects active task
     *
     * @param {string} payload
     */
    selectActiveTask(payload: string): void {
        this.props.selectTaskInWizard(payload);
    }

    /**
     * Creates status icon element
     *
     * @param {StatusIconInfo} statusIconInfo
     * @return React.ReactElement
     */
    createStatusIcon(statusIconInfo: StatusIconInfo): React.ReactElement {
        const {statusClass, statusName}: StatusIconData = getStatusIconData(statusIconInfo);
        return (
            <div
                title={statusName}
                className={`df-task-list-icon ${statusClass}`}
            />
        );
    }

    /**
     * react-virtualized item renderer
     *
     * @param {boolean} validTask
     * @param {number} index
     * @param {CSSProperties} style
     */
    tasksRowRenderer({index, style}: ListRowProps): React.ReactNode {
        const {
            tasks, activeTask, translate, validTaskMetadata,
        }: Props = this.props;
        const {name, status, payload}: TaskEntity = tasks[index];
        const isActiveTask: boolean = activeTask?.id === payload;

        return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
                key={index}
                style={style}
                onClick={this.selectActiveTask.bind(this, payload)}
                role="button"
                tabIndex={0}
                className="df-task-list-row"
                onKeyDown={this.onSelectTask.bind(this, payload)}
            >
                <div className={`df-task-list-item ${isActiveTask && "df-item-selected"}`}>
                    <Tooltip className="df-task-list-text" title={name}>{name}</Tooltip>
                    {this.createStatusIcon({
                        status,
                        isActiveTask,
                        activeTaskInvalid: (activeTask?.valid === false || !validTaskMetadata),
                        validTask: true,
                        translationPath: "components.TaskChanger.TaskChanger",
                        translate,
                    })}
                </div>
            </div>
        );
    }

    render() {
        const {translationPath, rowHeight}: typeof TaskList = TaskList;
        const {tasks, activeTask, validTaskMetadata}: Props = this.props;
        const {length: tasksLength}: TaskEntity[] = tasks;
        const contentHeight: number = (tasksLength + 1) * rowHeight; // (num of tasks + margins) * row height

        return (
            <div className="df-task-list" style={{height: contentHeight}}>
                <div className="df-task-list-title">
                    <Translate id={`${translationPath}.taskList.text`}/>
                </div>
                <AutoSizer>
                    {({height: autoSizerHeight, width: autoSizerWidth}: Size): React.ReactNode => (
                        <List
                            rowCount={tasksLength}
                            rowHeight={rowHeight}
                            height={autoSizerHeight}
                            width={autoSizerWidth}
                            rowRenderer={this.tasksRowRenderer}
                            activeTask={activeTask}
                            tasks={tasks}
                            validTaskMetadata={validTaskMetadata}
                            tabIndex={-1}
                        />
                    )}
                </AutoSizer>
            </div>
        );
    }
}

function mapStateToProps(
    {
        localize,
        job: {
            tasks,
            activeTask,
        },
        project,
        schema: {
            parsedSchemas,
            elementsValuesMap,
        },
    }: StoreState,
): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: Nullable<EditorType> = parsedSchemas[schemaKey] as EditorType;
    const translate: TranslateFunction = getTranslate(localize);
    const formErrorsPayload: GetFormErrorsPayload = {
        elementsValuesMap,
        schemaKey,
        editor,
        translate,
        jobId: historyParams?.jobId,
        tasks,
    };
    const validTaskMetadata: boolean = isTaskMetadataValid(formErrorsPayload, activeTask);

    return {
        translate,
        tasks,
        activeTask,
        editor,
        elementsValuesMap,
        validTaskMetadata,
    };
}

const mapDispatchToProps: DispatchProps = {
    selectTaskInWizard,
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskList);
